import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

interface AuthShellProps {
  title: string
  subtitle: string
  children: ReactNode
  backTo?: string
  backLabel?: string
}

/**
 * Shared full-screen frame for the auth pages (forgot/reset password). Mirrors
 * the LoginPage shell — lake background, layered deepwater overlays, gold
 * hairline — so the reset flow feels native to the sign-in experience.
 */
export const AuthShell = ({
  title,
  subtitle,
  children,
  backTo = '/login',
  backLabel = 'Back to sign in',
}: AuthShellProps) => (
  <div
    className="min-h-screen w-full flex items-center justify-center p-4 relative overflow-hidden bg-deepwater"
    style={{
      backgroundImage: 'url(/fonts/images/login_background.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
    }}
  >
    <div
      className="absolute inset-0 bg-gradient-to-b from-deepwater/85 via-deepwater/55 to-deepwater/95"
      aria-hidden="true"
    />
    <div
      className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(201,160,82,0.10),transparent_55%)]"
      aria-hidden="true"
    />
    <div
      className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent"
      aria-hidden="true"
    />
    <div className="relative z-10 w-full max-w-md">
      <Link
        to={backTo}
        className="inline-flex items-center gap-1 text-blue-100 hover:text-white text-sm mb-6"
      >
        <ArrowLeft size={14} /> {backLabel}
      </Link>
      <div className="text-center mb-8">
        <h1 className="serif-font text-3xl font-bold text-white mb-1">{title}</h1>
        <p className="text-blue-100/80 text-sm">{subtitle}</p>
      </div>
      <div className="glass-effect bg-white/10 border-white/20 rounded-2xl p-7 shadow-2xl">
        {children}
      </div>
    </div>
  </div>
)

export default AuthShell
